import { useEffect, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useObserver } from 'mobx-react-lite';
import { Theme, useMediaQuery } from '@material-ui/core';
import { datadogLogs } from '@datadog/browser-logs';

import useCart from 'hooks/use-cart';
import useErnie from 'shared/hooks/use-ernie';
import { useCartPricing } from 'src/hooks/use-cart-pricing/use-cart-pricing';
import { useListPriceTreatment } from 'src/hooks/use-list-price-treatment';
import { usePriceCartCalcs } from 'src/hooks/use-price-cart-calcs';
import useUI from 'hooks/use-ui';
import { useCartRecommendationsCarousel } from 'src/components/cart/cart-dropdown/cart-recommendations-carousel/use-cart-recommendations-carousel';
import { cartItemHasDiscount } from 'src/components/cart-item/use-cart-item-discount';
import { deriveDiscountBadge } from 'src/components/cart-item/derive-discount-badge';
import { deriveOriginalPriceLabel } from 'src/components/cart-item/derive-original-price-label';
import { findSingleWeightItem } from 'src/components/modals/add-to-cart';
import { useProductPricing } from 'src/hooks/use-product-pricing';
import { useQuickAddToCart } from 'src/components/product-carousel/use-quick-add-to-cart';
import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import type CartStore from 'src/state/cart';
import type CartItemStore from 'src/state/cart-item';
import type { Cart, CartItem, Loadable, Topper } from 'src/concierge/contract';
import { getQuantityRemaining, imageToUse } from 'shared/helpers/products';
import { formatCurrency } from 'shared/helpers/utils';
import { groupOptionsWithPrices } from 'src/utils/helpers/product';
import type { Product } from 'src/utils/helpers/product';

export type LineMapContext = {
  menuType: string | undefined;
  isKiosk: boolean;
  /** AND-ed with hasDiscount for the per-item hideStrikethrough flag. */
  hideStrikethroughGlobal: boolean;
  /** `ecomm.display-outlet-collections-consumer.rollout` LD flag. */
  outletCollectionsEnabled: boolean;
  /** Returns line totals (qty factored in) via big.js + receipt-item when ready. */
  getItemPrice: ReturnType<typeof useCartPricing>['getItemPrice'];
  hideDiscounts: boolean;
  /** Gates receipt-item-driven pricing. */
  priceCartEnabled: boolean;
  /** `ecomm.use-list-price-treatment.rollout` — wraps the original price label. */
  listPriceEnabled: boolean;
  /** From `useListPriceTreatment()` — "List $X" wrapper when the treatment is on. */
  formatStandardPrice: (price: string) => string;
  /** xs breakpoint — the badge text has a shorter mobile variant. */
  isMobile: boolean;
  isMedical: boolean;
  /** Drives the cart-line price skeleton, mirroring marketplace's cart-item. */
  isCalculating: boolean;
};

type TopperAddDeps = {
  quickAddToCart: ReturnType<typeof useQuickAddToCart>['handleAddToCart'];
  getPriceOptions: ReturnType<typeof useProductPricing>['getPriceOptions'];
};

// Mirrors `useCartItemDiscount` exactly — the hook itself can't be called in a
// loop, but its derivation is pure once the upstream hooks resolve at hook top.
// Exported for testing.
export function mapCartLine(item: CartItemStore, ctx: LineMapContext): CartItem {
  const { product } = item;
  // menuType can be undefined during transitions — fall back to rec.
  const weightOptions = groupOptionsWithPrices({
    product,
    menuType: ctx.menuType ?? 'rec',
    isKiosk: ctx.isKiosk,
  });

  const maxQuantity = getQuantityRemaining(product, item.option, { isKiosk: ctx.isKiosk });
  const outletInfo = product.outletCollectionInfo;
  const outletCollectionInfo =
    ctx.outletCollectionsEnabled && outletInfo?.endDate && outletInfo.timerMessage
      ? { endDate: outletInfo.endDate, timerMessage: outletInfo.timerMessage }
      : undefined;

  const { originalPrice, adjustedPrice, bogoDiscountedPrice, calculationMethod } = ctx.getItemPrice(item);
  const isWeightOnSale = cartItemHasDiscount({
    product,
    weight: item.option || 'N/A',
    adjustedPrice,
    originalPrice,
    isMedical: ctx.isMedical,
    onlyComparePrices: ctx.priceCartEnabled,
  });
  const hasDiscount = !ctx.hideDiscounts && (!!bogoDiscountedPrice || isWeightOnSale);
  const showOriginalPrice = !ctx.hideDiscounts || (!bogoDiscountedPrice && !isWeightOnSale);
  const showDiscountedPrice = !ctx.hideDiscounts && (!!bogoDiscountedPrice || isWeightOnSale);

  const lineOriginalTotalLabel = deriveOriginalPriceLabel({
    originalPrice,
    hasDiscount,
    listPriceEnabled: ctx.listPriceEnabled,
    formatStandardPrice: ctx.formatStandardPrice,
  });
  const lineDiscountedTotalLabel = formatCurrency(bogoDiscountedPrice ?? adjustedPrice);

  const { discountBadgeText, type } = deriveDiscountBadge({
    originalPrice,
    bogoDiscountedPrice,
    discountedPrice: adjustedPrice,
    item,
    shouldHideDiscounts: ctx.hideDiscounts,
    isWeightOnSale,
    isMobile: ctx.isMobile,
    isPriceCartEnabled: ctx.priceCartEnabled,
    calculationMethod,
  });

  return {
    // `item.key` is marketplace's compound `${productId}-${option}-${additionalOption}`.
    lineKey: item.key,
    additionalOption: item.additionalOption,
    brand: product.brand?.name ?? undefined,
    image: imageToUse(product) ?? '',
    maxQuantity,
    name: product.name,
    option: item.option || undefined,
    outletCollectionInfo,
    productId: product.id ?? '',
    quantity: item.quantity,
    strain: product.strainType ?? undefined,
    weightOptions,
    lineOriginalTotalLabel,
    lineDiscountedTotalLabel,
    discountBadge: discountBadgeText && type ? { text: discountBadgeText, type } : null,
    hasDiscount,
    showOriginalPrice,
    showDiscountedPrice,
    hideStrikethrough: ctx.hideStrikethroughGlobal && hasDiscount,
  };
}

// Exported for testing.
export function mapTopper(product: Product, deps: TopperAddDeps): Topper {
  // Same single-entry-point routing the V2 cart's "Before You Go" carousel uses.
  const add = async (): Promise<void> => {
    await deps.quickAddToCart(product, 'concierge');
  };

  // First option's effective price, menuType-aware (med vs rec) and respecting
  // sale specials — same source as the V2 product card.
  const firstOption = deps.getPriceOptions(product)[0];
  const price = firstOption?.special?.price ?? firstOption?.price ?? 0;

  return {
    id: product.id ?? '',
    brand: product.brand?.name ?? undefined,
    name: product.Name ?? product.name ?? '',
    image: imageToUse(product) ?? '',
    price,
    requiresWeightSelection: !findSingleWeightItem(product),
    add,
  };
}

// Exported for testing.
export function mapCartFromStore(
  cartStore: CartStore | undefined,
  ctx: LineMapContext,
  toppers: Loadable<Topper[]>,
  onCorruptedLine?: (item: CartItemStore) => void
): Cart | undefined {
  if (!cartStore) return undefined;
  const cb = cartStore.costBreakdown;
  const items = Object.values(cartStore.items)
    .filter((item): item is CartItemStore => item !== undefined)
    .flatMap((item) => {
      try {
        return [mapCartLine(item, ctx)];
      } catch (error) {
        // Containment: a single malformed line (e.g. a stale/invalid option whose
        // price can't be resolved) must degrade to dropping that one line, never
        // take down the whole ConciergeHost via an unhandled render throw.
        datadogLogs.logger.error('concierge: failed to map cart line', {
          // item.id is CartItem's own nullable derivation of product.id — avoids a
          // second throw here if product itself is the malformed part.
          productId: item.id ?? 'unknown',
          option: item.option,
          error: error instanceof Error ? error.message : String(error),
        });
        onCorruptedLine?.(item);
        return [];
      }
    });

  return {
    discount: cb.credit ?? 0,
    items,
    itemCount: items.reduce((sum, i) => sum + i.quantity, 0),
    subtotal: cb.subtotal ?? 0,
    tax: cb.tax ?? 0,
    total: cb.total ?? 0,
    toppers,
    isCalculating: ctx.isCalculating,
  };
}

/** Aggregates marketplace cart + recommendations into the slim concierge `Cart`
 * shape. All marketplace coupling lives here so the remote stays decoupled. */
export function useConciergeCart(): Cart | undefined {
  const cartStore = useCart();
  const UI = useUI();
  const { handleAddToCart: quickAddToCart } = useQuickAddToCart();
  const { getPriceOptions } = useProductPricing();
  const { hideStrikethrough: hideStrikethroughGlobal, listPriceEnabled, formatStandardPrice } = useListPriceTreatment();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));
  const { getItemPrice } = useCartPricing();
  const showErnie = useErnie();
  const dispensary = useObserver(() => cartStore?.dispensary);
  const hideDiscounts = useHideDiscountFromConsumer(dispensary);
  const priceCartEnabled = usePriceCartCalcs();
  const flags = useFlags();
  const outletCollectionsEnabled = Boolean(flags['ecomm.display-outlet-collections-consumer.rollout']);

  const menuType = useObserver(() => cartStore?.menuType);
  const isKiosk = useObserver(() => UI?.isKiosk ?? false);
  const isMedical = useObserver(() => cartStore?.isMedical ?? false);
  const isCalculating = useObserver(() => cartStore?.isCalculating ?? false);

  const recommendations = useCartRecommendationsCarousel();
  // TODO(ECOMM-10754): include sponsored + wire trackers once Brands aligns.
  const organicProducts = useMemo(() => recommendations.products.filter((p) => !p.adTrackers), [
    recommendations.products,
  ]);

  // Memo the topper array so `Topper.add` refs stay stable across host renders.
  const toppersData = useMemo(
    () => organicProducts.map((product) => mapTopper(product, { quickAddToCart, getPriceOptions })),
    [organicProducts, quickAddToCart, getPriceOptions]
  );

  const toppers: Loadable<Topper[]> = useMemo(() => ({ data: toppersData, loading: recommendations.loading }), [
    toppersData,
    recommendations.loading,
  ]);

  // Wrap the derivation so MobX item / costBreakdown updates propagate.
  const corruptedLineKeys: string[] = [];
  const cart = useObserver(() =>
    mapCartFromStore(
      cartStore,
      {
        menuType,
        isKiosk,
        hideStrikethroughGlobal,
        outletCollectionsEnabled,
        getItemPrice,
        hideDiscounts,
        priceCartEnabled,
        listPriceEnabled,
        formatStandardPrice,
        isMobile,
        isMedical,
        isCalculating,
      },
      toppers,
      (item) => corruptedLineKeys.push(item.key)
    )
  );

  // No deps: runs every render, no-ops unless this pass dropped a line. Removing
  // it re-triggers the observer above with a consistent items/costBreakdown, so
  // this settles after one cleanup pass.
  useEffect(() => {
    if (corruptedLineKeys.length === 0) return;
    corruptedLineKeys.forEach((lineKey) => cartStore?.removeItem(lineKey));
    showErnie('An item in your cart is no longer available and was removed.', 'danger');
  });

  return cart;
}
